// admin/ManageSubscribers.jsx
// View all subscribed emails.

import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, deleteDoc, updateDoc, orderBy, query } from "firebase/firestore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { db } from "../../firebase/config";
import { formatDate } from "../../utils/helpers";

export default function ManageSubscribers() {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [updating, setUpdating] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmToggleStatus, setConfirmToggleStatus] = useState(null);

    async function fetchSubscribers() {
        setLoading(true);
        try {
            const snap = await getDocs(query(collection(db, "subscribers"), orderBy("subscribedAt", "desc")));
            setSubscribers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error("Failed to fetch subscribers:", err);
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(id) {
        try {
            await deleteDoc(doc(db, "subscribers", id));
            setSubscribers((prev) => prev.filter((s) => s.id !== id));
        } catch (err) {
            console.error(err);
        } finally {
            setConfirmDelete(null);
        }
    }

    async function handleStatusToggle(id, currentlyDisabled) {
        try {
            setUpdating(id);
            await updateDoc(doc(db, "subscribers", id), { isDisabled: !currentlyDisabled });
            setSubscribers((prev) => prev.map((s) => s.id === id ? { ...s, isDisabled: !currentlyDisabled } : s));
        } catch (err) {
            console.error(err);
        } finally {
            setUpdating(null);
            setConfirmToggleStatus(null);
        }
    }

    useEffect(() => { fetchSubscribers(); }, []);

    const filtered = subscribers.filter(
        (s) =>
            !search ||
            (s.email || "").toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold text-gray-900">Subscribers</h1>
                    <p className="text-sm text-gray-500">
                        {subscribers.length} total subscriber{subscribers.length !== 1 ? "s" : ""}
                    </p>
                </div>
            </div>

            {/* Search */}
            <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email…"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-72"
            />

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 animate-pulse">Loading subscribers…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-gray-400">No subscribers found.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                                <tr>
                                    <th className="text-left px-5 py-3">Email</th>
                                    <th className="text-left px-5 py-3">Status</th>
                                    <th className="text-left px-5 py-3">Subscribed At</th>
                                    <th className="text-center px-5 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filtered.map((sub) => (
                                    <tr key={sub.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-5 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${sub.isDisabled ? "bg-gray-100 text-gray-500" : "bg-pink-100 text-pink-700"}`}>
                                                    {(sub.email || "?")[0].toUpperCase()}
                                                </div>
                                                <p className={`font-medium ${sub.isDisabled ? "text-gray-500 line-through" : "text-gray-800"}`}>{sub.email}</p>
                                            </div>
                                        </td>
                                        <td className="px-5 py-3">
                                            {sub.isDisabled ? (
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 bg-red-100 px-2 py-0.5 rounded-md">Disabled</span>
                                            ) : (
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-green-600 bg-green-100 px-2 py-0.5 rounded-md">Active</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-gray-500">
                                            {formatDate(sub.subscribedAt) || "—"}
                                        </td>
                                        <td className="px-5 py-3 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                  onClick={() => setConfirmToggleStatus(sub)}
                                                  disabled={updating === sub.id}
                                                  className={`text-xs px-3 py-1 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                                                    sub.isDisabled
                                                      ? "bg-green-50 text-green-700 hover:bg-green-100"
                                                      : "bg-orange-50 text-orange-600 hover:bg-orange-100"
                                                  }`}
                                                >
                                                  {sub.isDisabled ? "Enable" : "Disable"}
                                                </button>
                                                <button onClick={() => setConfirmDelete(sub)} title="Delete" disabled={updating === sub.id}
                                                  className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 font-medium transition-colors disabled:opacity-50">
                                                  <DeleteOutlineIcon fontSize="small" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Info note */}
            <p className="text-xs text-gray-400">
                💡 This list contains all users who subscribed via the homepage newsletter section. Disabled subscribers will not receive communications.
            </p>

            {/* Confirmation Modals */}
            {confirmDelete && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
                        <p className="text-4xl mb-3">🗑️</p>
                        <h3 className="font-bold text-gray-900 mb-2">Delete Subscriber?</h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Are you sure you want to permanently delete <strong>{confirmDelete.email}</strong>?
                        </p>
                        <div className="flex gap-3">
                            <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
                            <button onClick={() => handleDelete(confirmDelete.id)} className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700">Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {confirmToggleStatus && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
                        <p className="text-4xl mb-3">{confirmToggleStatus.isDisabled ? "✅" : "🚫"}</p>
                        <h3 className="font-bold text-gray-900 mb-2">{confirmToggleStatus.isDisabled ? "Enable Subscriber?" : "Disable Subscriber?"}</h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Are you sure you want to {confirmToggleStatus.isDisabled ? "enable" : "disable"} <strong>{confirmToggleStatus.email}</strong>?
                        </p>
                        <div className="flex gap-3">
                            <button onClick={() => setConfirmToggleStatus(null)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">Cancel</button>
                            <button onClick={() => handleStatusToggle(confirmToggleStatus.id, confirmToggleStatus.isDisabled)} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white ${confirmToggleStatus.isDisabled ? "bg-green-600 hover:bg-green-700" : "bg-orange-600 hover:bg-orange-700"}`}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
